import {
  formatDuration,
  formatDate,
  formatTime,
  getDisplayAddress,
} from "../../containers/Tracking/utils";
import { createMapInfoDialog } from "../QIMapInfoDialog";

/**
 * Creates a standstill popup using QIMapInfoDialog DOM factory.
 *
 * Returns the full dialog result (container + open/close controls) so callers
 * like VehicleTrack can toggle between pill and popover on map overlays.
 *
 * @param {Object} standstill - Standstill data object
 * @param {string} vehicleRegistration - Vehicle registration number
 * @param {string} [timezone] - Optional IANA timezone identifier
 * @returns {{ container: HTMLElement, open: Function, close: Function }}
 */
export function createStandstillPopupElement(standstill, vehicleRegistration, timezone = undefined) {
  if (!standstill) {
    const fallback = document.createElement("div");
    fallback.textContent = "No standstill data available";
    return { container: fallback, open: () => {}, close: () => {} };
  }

  const location = getDisplayAddress(standstill.gps);
  const date = formatDate(standstill.startTime, undefined, timezone);
  const time = formatTime(standstill.startTime, undefined, timezone);
  const duration = formatDuration(standstill.duration);

  return createMapInfoDialog({
    pill: { icon: "bell-z", text: duration },
    header: { icon: "bell-z", title: "Standstill", badge: vehicleRegistration },
    body: location,
    footer: [
      { text: date, className: "qi-map-info-dialog_footer_date", tag: "time" },
      { text: time, className: "qi-map-info-dialog_footer_time", tag: "time" },
      { text: duration, className: "qi-map-info-dialog_footer_duration", tag: "data" },
    ],
  });
}

/**
 * Generates an HTML string for a standstill popup.
 * Convenience wrapper for contexts that require an HTML string
 * (e.g. Leaflet's bindPopup).
 *
 * @param {Object} standstill - Standstill data object
 * @param {string} vehicleRegistration - Vehicle registration number
 * @param {string} [timezone] - Optional IANA timezone identifier
 * @returns {string} HTML string for popup content
 */
export function generateStandstillPopupHTML(standstill, vehicleRegistration, timezone = undefined) {
  const { container } = createStandstillPopupElement(standstill, vehicleRegistration, timezone);
  return container.outerHTML;
}
